import { useEffect, useState } from 'react';
import {
  CUSTOM_VALUE_LABEL,
  NATURAL_DELIVERY_LABEL,
  ZERO_PERCENT_PEAK_LABEL
} from './constants';
import styled from 'styled-components';
import { SectionTitle } from '../Title';
import { Divider } from '../Divider';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { RootState } from '../../redux/store';
import { getMinimumPeak, getPeakLoading } from '../../services/calculate';
import { MONTH_LOWERCASE, MONTH_PASCAL, ZERO_PEAK } from '../../constants';
import {
  updatePeak,
  updatePeakLoading
} from '../../redux/features/calculated/calculatedSlice';
import {
  mapPascalMonthToLowercaseMonth,
  formatLoadingDisplay
} from '../../util';
import ContentContainer from '../containers/ContentContainer';
import PeakPercentageLabels from './PeakPercentageLabels';
import PeakPercentageSlider from './PeakPercentageSlider';

interface PeakPercentageProps {
  months: MONTH_PASCAL[] | undefined;
  batchUpdateTotals: (month: MONTH_LOWERCASE, totalPeakLoading: number) => void;
}

const StyledContentContainer = styled(ContentContainer)`
  gap: 28px !important;
`;

const PeakPercentage = ({ months, batchUpdateTotals }: PeakPercentageProps) => {
  const dispatch = useAppDispatch();
  const { demographic } = useAppSelector((state: RootState) => state.detail);
  const { peakLoading } = useAppSelector(
    (state: RootState) => state.spreadsheet
  );
  const { calculated } = useAppSelector((state: RootState) => state);

  const [maximum, setMaximum] = useState<number>(90);
  const [minimum, setMinimum] = useState<number>(20);
  const [loadingPercentage, setLoadingPercentage] = useState<number>(0);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [value, setValue] = useState<number>(0);

  const handleSliderChange = (_: Event, newValue: number | number[]): void => {
    if (!demographic) {
      return;
    }

    const castedValue = newValue as number;
    updateTotalLoadingPercentage(castedValue);
    setValue(castedValue);
  };

  const updateTotalLoadingPercentage = (selectedValue: number) => {
    const percentage = selectedValue / 100;
    const loading = getPeakLoading(peakLoading, demographic, percentage);

    if (loading !== null) {
      const loadingPercentage = formatLoadingDisplay(loading);
      setLoadingPercentage((loadingPercentage * 100) / 100);
    }
  };

  const handleLabelSelected = (alignment: string | null): void => {
    switch (alignment) {
      case CUSTOM_VALUE_LABEL:
        setSliderToMiddleAndEnable();
        break;
      case ZERO_PERCENT_PEAK_LABEL:
        setSliderToStartAndDisable();
        break;
      case NATURAL_DELIVERY_LABEL:
        setSliderToEndAndDisable();
        break;
      default:
        break;
    }
  };

  const setSliderToMiddleAndEnable = (): void => {
    if (!demographic || !peakLoading) {
      return;
    }

    const minimumPeak = getMinimumPeak(peakLoading, demographic);

    setIsDisabled(false);
    setValue(minimumPeak * 100);
    updateTotalLoadingPercentage(minimumPeak * 100);
  };

  const setSliderToStartAndDisable = (): void => {
    setIsDisabled(true);
    setValue(ZERO_PEAK);
    updateTotalLoadingPercentage(ZERO_PEAK);
  };

  const setSliderToEndAndDisable = (): void => {
    setIsDisabled(true);
    setValue(maximum);
    updateTotalLoadingPercentage(maximum);
  };

  const batchUpdatePeakPercentages = (): void => {
    if (months) {
      for (const month of months) {
        const lowercaseMonth = mapPascalMonthToLowercaseMonth(month);

        const updatedPeak = updatePeak({ month: lowercaseMonth, peak: value });
        const updatedPeakLoading = updatePeakLoading({
          peakLoading: loadingPercentage,
          month: lowercaseMonth
        });

        batchUpdateTotals(lowercaseMonth, loadingPercentage);
        dispatch(updatedPeak);
        dispatch(updatedPeakLoading);
      }
    }
  };

  const setUpPageOnLoad = () => {
    if (demographic && months) {
      const minimumPeak = getMinimumPeak(peakLoading, demographic);

      const lowercaseMonth = mapPascalMonthToLowercaseMonth(months[0]);
      const existingValue = calculated[lowercaseMonth].peak;

      setMinimum(minimumPeak * 100);

      if (existingValue === 0) {
        setValue(minimumPeak * 100);
        updateTotalLoadingPercentage(minimumPeak);
      } else {
        setValue(existingValue);
        updateTotalLoadingPercentage(existingValue);
      }
    }
  };

  useEffect(() => {
    setUpPageOnLoad();
  }, []);

  useEffect(() => {
    if (months) {
      batchUpdatePeakPercentages();
    }
  }, [maximum, minimum, loadingPercentage]);

  return (
    <>
      <StyledContentContainer>
        <SectionTitle
          title="Peak Percentage"
          loadingPercentage={loadingPercentage}
        />
        <PeakPercentageLabels
          maximumPeakPercentage={maximum}
          onLabelSelect={handleLabelSelected}
        />
        <Divider />
        <PeakPercentageSlider
          minimum={minimum}
          maximum={maximum}
          isDisabled={isDisabled}
          value={value}
          onChange={handleSliderChange}
        />
      </StyledContentContainer>
    </>
  );
};

export default PeakPercentage;
